import { useEffect, useRef, useState } from "react"

// Scroll positions can be fractional on scaled displays; anything under a pixel counts as the edge.
const EDGE_TOLERANCE_PX = 1

/**
 * Drive a horizontally scrolling strip whose container fades the edge the content continues behind.
 *
 * Attach `containerRef` to the clipping container and `scrollRef` to the scrolling element inside it. While
 * the content overflows, vertical wheel movement over the strip scrolls it horizontally instead of the page;
 * horizontal wheel and trackpad movement scrolls it natively.
 */
export default function useHorizontalScrollFade<T extends HTMLElement>() {
    const containerRef = useRef<HTMLDivElement>(null)
    const scrollRef = useRef<T>(null)

    const [isLeftClipped, setIsLeftClipped] = useState(false)
    const [isRightClipped, setIsRightClipped] = useState(false)

    useEffect(() => {
        const scrollElement = scrollRef.current

        if (scrollElement) {
            const measure = () => {
                const { scrollLeft, scrollWidth, clientWidth } = scrollElement
                setIsLeftClipped(scrollLeft > EDGE_TOLERANCE_PX)
                setIsRightClipped(scrollWidth - scrollLeft - clientWidth > EDGE_TOLERANCE_PX)
            }

            // React registers wheel listeners as passive, so the redirect needs a native non-passive one to
            // be allowed to preventDefault; horizontal gestures and shift-wheel are left to the browser.
            const onWheel = (event: WheelEvent) => {
                const isVerticalGesture = Math.abs(event.deltaY) > Math.abs(event.deltaX)
                const canScroll = scrollElement.scrollWidth > scrollElement.clientWidth

                if (isVerticalGesture && canScroll && !event.shiftKey) {
                    event.preventDefault()
                    scrollElement.scrollLeft += event.deltaY
                }
            }

            const resizeObserver = new ResizeObserver(measure)
            resizeObserver.observe(scrollElement)
            scrollElement.addEventListener("scroll", measure, { passive: true })
            scrollElement.addEventListener("wheel", onWheel, { passive: false })

            return () => {
                resizeObserver.disconnect()
                scrollElement.removeEventListener("scroll", measure)
                scrollElement.removeEventListener("wheel", onWheel)
            }
        }
    }, [scrollRef.current])

    return { containerRef, scrollRef, isLeftClipped, isRightClipped }
}
